import React, { useEffect, useState } from 'react';
import api from '../api';
import Modal from '../components/Modal';
import {
  ActionBar,
  Button,
  FormRow,
  Input,
  Label,
  Muted,
  Page,
  PageHeader,
  PageHeaderMain,
  Subtitle,
  Surface,
  SurfaceBody,
  SurfaceHeader,
  Table,
  TableWrap,
  Title,
  Toast,
  Toolbar,
} from '../components/ui';

interface TwitchUser {
  _id: string;
  username: string;
  displayName: string;
  balance: number;
  lastSeenAt: string;
}

const UsersPage: React.FC = () => {
  const [users, setUsers] = useState<TwitchUser[]>([]);
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState<TwitchUser | null>(null);
  const [balance, setBalance] = useState('0');
  const [toast, setToast] = useState('');

  const load = async () => {
    const { data } = await api.get('/users', { params: { search, limit: 50 } });
    setUsers(data.users);
  };

  useEffect(() => { load(); }, []);

  const saveBalance = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editing) return;
    await api.put(`/users/${editing._id}`, { balance: parseInt(balance, 10) || 0 });
    setEditing(null);
    setToast('Genialitos actualizados');
    load();
  };

  return (
    <Page>
      <PageHeader>
        <PageHeaderMain>
          <Title>Usuarios Twitch</Title>
          <Subtitle>Buscar y editar Genialitos de la comunidad.</Subtitle>
        </PageHeaderMain>
      </PageHeader>

      {toast && <Toast>{toast}</Toast>}

      <Surface>
        <SurfaceHeader>
          <Toolbar style={{ flex: 1 }}>
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Username o display name..."
              style={{ maxWidth: 360 }}
              onKeyDown={(e) => { if (e.key === 'Enter') load(); }}
            />
            <Button $size="sm" type="button" onClick={load}>Buscar</Button>
            <Muted style={{ margin: 0 }}>{users.length} resultados</Muted>
          </Toolbar>
        </SurfaceHeader>
        <SurfaceBody $flush>
          <TableWrap>
            <Table>
              <thead>
                <tr>
                  <th>Usuario</th>
                  <th>Display</th>
                  <th style={{ width: '12%' }}>Genialitos</th>
                  <th style={{ width: '18%' }}>Última actividad</th>
                  <th style={{ width: '12%', textAlign: 'right' }}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u._id}>
                    <td>{u.username}</td>
                    <td>{u.displayName}</td>
                    <td>{u.balance}</td>
                    <td>{new Date(u.lastSeenAt).toLocaleString('es-AR')}</td>
                    <td style={{ textAlign: 'right' }}>
                      <ActionBar>
                        <Button
                          $variant="ghost"
                          $size="sm"
                          type="button"
                          onClick={() => { setEditing(u); setBalance(String(u.balance)); }}
                        >
                          Editar
                        </Button>
                      </ActionBar>
                    </td>
                  </tr>
                ))}
                {!users.length && (
                  <tr><td colSpan={5} style={{ textAlign: 'center', padding: '1.5rem' }}>Sin usuarios</td></tr>
                )}
              </tbody>
            </Table>
          </TableWrap>
        </SurfaceBody>
      </Surface>

      <Modal
        open={!!editing}
        title={editing ? `Editar Genialitos · ${editing.displayName}` : 'Editar'}
        onClose={() => setEditing(null)}
        footer={editing ? (
          <>
            <Button $variant="ghost" type="button" onClick={() => setEditing(null)}>Cancelar</Button>
            <Button type="submit" form="balance-form">Guardar</Button>
          </>
        ) : undefined}
      >
        {editing && (
          <form id="balance-form" onSubmit={saveBalance}>
            <FormRow>
              <Label>Genialitos</Label>
              <Input type="number" value={balance} onChange={(e) => setBalance(e.target.value)} />
            </FormRow>
          </form>
        )}
      </Modal>
    </Page>
  );
};

export default UsersPage;
